import * as React from 'react';
import Helmet from 'react-helmet';
import LegacyPanel from '../../../legacyCore/LegacyPanel';
import StaticMap from '../../../legacyCore/StaticMap';
import AppointmentInfo from '../../../shared/components/AppointmentInfo';
import CancelAppointmentForm, {
  AppointmentImminentWarning,
  AppointmentPassedWarning,
  AppointmentUncancelableWarning,
} from '../../../shared/components/CancelAppointmentForm';
import Unauthorized from '../../../shared/components/Unauthorized';
import { hasPassed, isCancelable, isImminent } from '../../../shared/utils/appointmentUtils';
import isForbidden from '../../../shared/utils/isForbidden';
import WidgetHeader from '../../components/WidgetHeader';
import WidgetLayout from '../../components/WidgetLayout';
import LoadingPage from '../LoadingPage';
import NotFoundPage from '../NotFoundPage';
import style from './CancelAppointmentPage.scss';

interface CancelAppointmentPageProps {
  data: {
    appointment: any;
    currentUser: any;
  };
  loading: boolean;
  error?: Error | null | undefined;
}

export default function CancelAppointmentPage({
  data,
  loading,
  error,
}: CancelAppointmentPageProps) {
  if (loading) return <LoadingPage />;
  if (isForbidden(error)) {
    return (
      <Unauthorized>
        <LoadingPage />
      </Unauthorized>
    );
  }
  if (error) return <WidgetLayout error={error} />;
  if (!data || !data.appointment) return <NotFoundPage />;

  const { appointment, currentUser } = data;
  const { provider } = appointment;
  const { office } = provider;
  const { phone } = office;
  let content;
  if (hasPassed(appointment)) {
    content = <AppointmentPassedWarning phone={phone} />;
  } else if (isImminent(appointment)) {
    content = <AppointmentImminentWarning phone={phone} />;
  } else if (isCancelable(appointment)) {
    content = (
      <CancelAppointmentForm appointment={appointment} currentUser={currentUser} isOnWidget />
    );
  } else {
    content = <AppointmentUncancelableWarning phone={phone} />;
  }
  return (
    <WidgetLayout header={<WidgetHeader title="Cancel your Appointment" showBackButton />}>
      <Helmet>
        <title>Cancel your Appointment</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className={style['cancel-appointment-page__row']}>
        <div className={style['cancel-appointment-page__main']}>
          <LegacyPanel mb={0}>{content}</LegacyPanel>
        </div>
        <div className={style['cancel-appointment-page__aside']}>
          <StaticMap
            className={style['cancel-appointment-page__map']}
            width="100%"
            height="192"
            zoom={12}
            markers={[
              {
                latitude: appointment.provider.office.latitude,
                longitude: appointment.provider.office.longitude,
              },
            ]}
          />
          <LegacyPanel px={0} mb={0}>
            <AppointmentInfo appointment={appointment} />
          </LegacyPanel>
        </div>
      </div>
    </WidgetLayout>
  );
}
